"use client";

import { AutocompleteInput } from "@/components/search/autocomplete-input";
import { useTalentSearchFilters } from "@/components/search/talent-search-filters-provider";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Icon } from "@/components/ui/icon";
import {
  CLEARANCE_LEVEL_LABELS,
  CLEARANCE_LEVELS,
  CLEARANCE_POLYGRAPH_LABELS,
  CLEARANCE_POLYGRAPHS,
  type ClearancePolygraph,
  isClearanceLevel,
  levelsHaveHigherLevels,
  splitLegacyClearanceHighlights,
} from "@/features/search/lib/clearances";
import { resolveHighlightEntry } from "@/features/search/lib/highlights";
import type { AutocompleteResult } from "@/lib/api/types";
import { cn } from "@/lib/utils";
import {
  ClearChipsButton,
  FilterChip,
  FilterFieldContainer,
  FilterFieldContent,
  FilterFieldLabel,
} from "../shared";

const OR_HIGHER = "or_higher";
const EXACT = "exact";

function FooterTrigger({ label, muted }: { label: string; muted?: boolean }) {
  return (
    <DropdownMenuTrigger asChild>
      <button type="button" className="flex min-w-0 cursor-pointer items-center gap-0.5">
        <span
          className={cn(
            "truncate text-[11px] tracking-[-0.1px] whitespace-nowrap",
            muted ? "text-ai/60" : "text-ai",
          )}
        >
          {label}
        </span>
        <Icon name="IconChevronDownMedium" size={11} className="shrink-0 text-ai" />
      </button>
    </DropdownMenuTrigger>
  );
}

function toggle<T extends string>(list: readonly T[], value: T): T[] {
  return list.includes(value)
    ? list.filter((v) => v !== value)
    : [...list, value];
}

export function FilterClearancesSection() {
  const { filters, updateFilter } = useTalentSearchFilters();
  const levels = filters.clearance_levels ?? [];
  const polygraphs = filters.clearance_polygraphs ?? [];
  const strict = filters.clearance_strict ?? false;
  const { highlights, legacyClearances } = splitLegacyClearanceHighlights(
    filters.highlight_types,
  );

  const setLevels = (next: typeof levels) => {
    updateFilter("clearance_levels", next.length > 0 ? next : undefined);
    if (next.length === 0) updateFilter("clearance_strict", undefined);
  };
  const setPolygraphs = (next: typeof polygraphs) =>
    updateFilter("clearance_polygraphs", next.length > 0 ? next : undefined);
  const removeLegacy = (id: string) => {
    const next = [
      ...highlights,
      ...legacyClearances.filter((h) => h.text !== id),
    ];
    updateFilter("highlight_types", next.length > 0 ? next : undefined);
  };

  const onSelect = (result: AutocompleteResult) => {
    if (isClearanceLevel(result.value)) setLevels(toggle(levels, result.value));
  };

  const polygraphLabel =
    polygraphs.length === 0
      ? "None"
      : polygraphs.map((p) => CLEARANCE_POLYGRAPH_LABELS[p] ?? p).join(", ");

  return (
    <>
      <FilterFieldContainer>
        <FilterFieldLabel>Security Clearance</FilterFieldLabel>
        <FilterFieldContent>
          <div className="flex flex-col">
            <AutocompleteInput
              variant="filter"
              field="clearance"
              placeholder="Select level..."
              flatBottom
              staticItems={CLEARANCE_LEVELS}
              selectedValues={levels}
              allowFreeText={false}
              onSelect={onSelect}
            />
            <div className="flex h-7 items-center justify-between rounded-b-md border border-t-0 border-contrast/15 bg-contrast/8 px-2">
              <div className="flex min-w-0 items-center gap-1">
                <span className="shrink-0 text-[11px] tracking-[-0.1px] text-foreground/60">
                  Polygraph
                </span>
                <DropdownMenu>
                  <FooterTrigger label={polygraphLabel} muted={polygraphs.length === 0} />
                  <DropdownMenuContent align="start" sideOffset={2} className="w-[200px]">
                    {CLEARANCE_POLYGRAPHS.map((poly) => (
                      <DropdownMenuCheckboxItem
                        key={poly.value}
                        checked={polygraphs.includes(poly.value)}
                        onCheckedChange={() =>
                          setPolygraphs(toggle(polygraphs, poly.value as ClearancePolygraph))
                        }
                        onSelect={(event) => event.preventDefault()}
                      >
                        {poly.label}
                      </DropdownMenuCheckboxItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              {levelsHaveHigherLevels(levels) && (
                <DropdownMenu>
                  <FooterTrigger label={strict ? "Exact level" : "Or higher"} />
                  <DropdownMenuContent align="end" sideOffset={2} className="w-[160px]">
                    <DropdownMenuRadioGroup
                      value={strict ? EXACT : OR_HIGHER}
                      onValueChange={(value) =>
                        updateFilter("clearance_strict", value === EXACT || undefined)
                      }
                    >
                      <DropdownMenuRadioItem value={OR_HIGHER}>Or higher</DropdownMenuRadioItem>
                      <DropdownMenuRadioItem value={EXACT}>Exact level</DropdownMenuRadioItem>
                    </DropdownMenuRadioGroup>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </div>
        </FilterFieldContent>
      </FilterFieldContainer>

      {levels.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {levels.map((level) => (
            <FilterChip
              key={level}
              label={CLEARANCE_LEVEL_LABELS[level] ?? level}
              onRemove={() => setLevels(levels.filter((l) => l !== level))}
            />
          ))}
          <ClearChipsButton onClick={() => setLevels([])} />
        </div>
      )}

      {legacyClearances.length > 0 && (
        <FilterFieldContainer>
          <FilterFieldLabel>From an older saved search</FilterFieldLabel>
          <FilterFieldContent>
            <div className="flex flex-wrap gap-1">
              {legacyClearances.map((h) => (
                <FilterChip
                  key={h.text}
                  label={resolveHighlightEntry(h.text, []).label}
                  onRemove={() => removeLegacy(h.text)}
                />
              ))}
            </div>
          </FilterFieldContent>
        </FilterFieldContainer>
      )}
    </>
  );
}
